import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Papa, ParseResult } from 'ngx-papaparse';
import { ButtonComponent } from "../button/button.component";
import { DropdownService } from '../../services/dropdown.service';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { KeyValueWithOrderPipe } from '../../pipes/key-value-with-order.pipe';
import { ModalComponent } from '../modal/modal.component';
import { Subscription } from 'rxjs';
import { ValidationService } from '../../services/validation.service';

@Component({
  selector: 'app-bulk-upload',
  imports: [NgIf, NgFor, ButtonComponent, NgxUiLoaderModule, KeyValueWithOrderPipe, NgClass],
  templateUrl: './bulk-upload.component.html',
  styleUrl: './bulk-upload.component.css'
})
export class BulkUploadComponent {
  public Object = Object;
  @Input() title: string = '';
  @Input() template_url: string = '';
  @Input() accepted_type: any = [];
  @Input() max_file_sizeMB: number = 5;
  @Input() enable_preview: boolean = true;
  @Input() bulk_columns: any[] = [];
  @Input() can_have_empty_columns = false; // If true, allows empty columns in the CSV
  @Input() button_title: any = ''
  @Input() cancel!: ModalComponent;
  @Input() extra_data: any = {};
  @Input() excluded_columns: string[] = [];
  @Input() disabled: boolean = false;

  @Output() submit = new EventEmitter<any>();     // Emits parsed data on submit
  @Output() error = new EventEmitter<any>();     // Emits error messages
  @Output() errorReportDownload = new EventEmitter<any>();     // Emits error report download request

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private modalCloseSub?: Subscription;

  preview_data: any[] = [];
  display_data: Array<{ [key: string]: any }> = [];
  file_upload_mode = true;
  error_message = '';
  is_dragging = false;
  is_uploading = false;
  dropdown_data: { [key: string]: any[] } = {};
  STRICT_VALIDATORS: any = {
    name: {
      regex: this.vs.name_pattern,
      message: (val: string, row: number, col: string) =>
        `Invalid Name "${val}" at row ${row} in Column Name "${col}".`
    },
    code: {
      regex: this.vs.code_pattern,
      message: (val: string, row: number, col: string) =>
        `Invalid Code "${val}" at row ${row} in Column Name "${col}".`
    },
    mobile: {
      regex: this.vs.contact_pattern,
      message: (val: string, row: number, col: string) =>
        `Invalid Mobile Number "${val}" at row ${row} in Column Name "${col}". Mobile number must be 10 digits`
    },
    aadhaar: {
      regex: this.vs.aadhar_pattern,
      message: (val: string, row: number, col: string) =>
        `Invalid Aadhaar Number "${val}" at row ${row} in Column Name "${col}". Aadhaar number must be 12 digits and cannot start with 0 or 1.`
    },
    pincode: {
      regex: this.vs.pincode_pattern,
      message: (val: string, row: number, col: string) =>
        `Invalid Pin Code "${val}" at row ${row} in Column Name "${col}".`
    },
    pan: {
      regex: this.vs.pan_pattern,
      message: (val: string, row: number, col: string) =>
        `Invalid PAN Number "${val}" at row ${row} in Column Name "${col}". `
    },
    gst: {
      regex_basic: this.vs.gst_pattern,
      regex_format: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/i,
      message: (val: string, row: number, col: string) =>
        `Invalid GST Number Added "${val}" at row ${row} in Column Name "${col}". `
    },
    email: {
      regex: this.vs.email_pattern,
      message: (val: string, row: number, col: string) =>
        `Invalid Email "${val}" at row ${row} in Column Name "${col}".`
    }
  };

  error_report: any[] = [];

  constructor(private papa: Papa, public gds: DropdownService, public vs: ValidationService) { }

  async ngOnInit() {
    const required_lists = this.bulk_columns.filter(c => c.type === 'link' && c.linked_list).map(c => c.linked_from).filter((value, index, self) => self.indexOf(value) === index);

    await this.gds.get_dropdown_list(required_lists);
    this.dropdown_data = this.gds.dropdown_list;

    if (this.cancel?.close_modal_clicked) {
      this.modalCloseSub = this.cancel.close_modal_clicked.subscribe(() => {
        this.reset_bulk_state();
      });
    }
  }


  download_template() {
    if (!this.bulk_columns || this.bulk_columns.length === 0) {
      this.emit_error('No columns defined for template.');
      return;
    }

    let columns_to_export = this.bulk_columns;
    if (this.excluded_columns.length > 0) {
      columns_to_export = this.bulk_columns.filter(col => !this.excluded_columns.includes(col.column_name));
    }

    const headers = columns_to_export.map(col => col.column_name);
    const sampleRow = columns_to_export.map(col => col.sample_value ?? '');

    let csvContent = headers.join(',') + '\n';
    if (sampleRow.some(val => val !== '')) {
      csvContent += sampleRow.join(',') + '\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${this.title} template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  on_file_change(event: any) {
    this.is_uploading = true;
    this.preview_data = [];
    const file = event.target.files[0];
    event.target.value = '';
    // if (!file || file.name.split('.').pop()?.toLowerCase() !== this.accepted_type.replace('.', '')) {
    //   this.emit_error('Please upload a valid CSV file!');
    //   return;
    // }
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const allowed = this.accepted_type.map((ext: any) => ext.replace('.', '').toLowerCase());

    if (!file || !allowed.includes(fileExt)) {
      this.emit_error('Please upload a valid file! (Only CSV, XLS, XLSX allowed)');
      this.is_uploading = false;
      return;
    }
    if (file.size > this.max_file_sizeMB * 1024 * 1024) {
      this.emit_error(`File size exceeds ${this.max_file_sizeMB}MB`);
      this.is_uploading = false;
      return;
    }

    const reader = new FileReader();

    reader.onload = (e: any) => {
      const csvData = e.target.result;

      this.papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
        complete: (result: ParseResult<any>) => {
          this.validate_and_emit(result.data);
        }
      });
    };
    reader.readAsText(file);
  }

  validate_and_emit(data: any[]) {
    if (!data || data.length === 0) {
      this.emit_error('Uploaded file is empty. Please verify the file and try again.');
      return;
    }

    if (!this.validate_column_names(data)) {
      return;
    }

    const parsed: any[] = [];
    const display: any[] = [];
    this.error_report = [];
    let has_any_error = false;

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      let row_isValid = true;
      const parsed_row: any = {};
      const display_row: any = {};
      const row_errors: string[] = [];

      let columns_to_validate = this.bulk_columns;
      if (this.excluded_columns.length > 0) {
        columns_to_validate = this.bulk_columns.filter(col => !this.excluded_columns.includes(col.column_name));
      }

      for (const config of columns_to_validate) {
        const raw_value = row[config.column_name]?.trim() ?? '';

        if (!raw_value) {
          if (!config.skip_validation && !this.can_have_empty_columns) {
            row_errors.push(`Empty cell at column "${config.column_name}"`);
            row_isValid = false;
            continue;
          }
          // If field is empty and skip_validation is true, continue to next column
          if (config.skip_validation) {
            display_row[config.column_name] = raw_value;
            continue;
          }
        }

        // Strict Validations
        if (config.strict_validation) {
          const strict_error = this.validate_strict_field(config, raw_value, i);
          if (strict_error) {
            row_errors.push(strict_error);
            row_isValid = false;
            continue;
          }
        }

        if (config.Dependency && config.Dependency.parent_return_as && config.Dependency.match_key) {
          const parent_value = parsed_row[config.Dependency.parent_return_as];
          const full_list = this.dropdown_data[config.linked_list] || [];

          if (!parent_value) {
            row_errors.push(`Invalid value in "${config.column_name}"`);
            row_isValid = false;
            continue;
          }

          // Filter list based on parent value
          const filtered_list = full_list.filter((item: any) => {
            const item_parent_value = this.getValueByPath(item, config.Dependency.match_key);
            return item_parent_value === parent_value;
          });

          // Now check if current raw_value exists in the FILTERED list
          const normalize = (val: any) => String(val ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
          const match = filtered_list.find((item: any) =>
            normalize(item?.title) == raw_value.toLowerCase() ||
            normalize(item?.name) == raw_value.toLowerCase()
          );

          if (!match) {
            row_errors.push(`Invalid value "${raw_value}" in column "${config.column_name}"`);
            row_isValid = false;
            continue;
          }
          // Use the match found in filtered list
          parsed_row[config.return_as] = match[config.returning_key];

          // Move to next config, skipping the standard check below
          continue;
        }

        // Generic Validations (Min/Max Length, Custom Regex)
        if (raw_value) {
          if (config.min_length && raw_value.length < config.min_length) {
            row_errors.push(config.min_length_error || `Minimum ${config.min_length} characters required in "${config.column_name}" column`);
            row_isValid = false;
            continue;
          }
          if (config.max_length && raw_value.length > config.max_length) {
            row_errors.push(config.max_length_error || `Maximum ${config.max_length} characters allowed in "${config.column_name}" column`);
            row_isValid = false;
            continue;
          }
          if (config.regex && config.regex instanceof RegExp) {
            if (!config.regex.test(raw_value)) {
              row_errors.push(config.regex_error || `Invalid format "${raw_value}" at row ${i + 1} in Column Name "${config.column_name}".`);
              row_isValid = false;
              continue;
            }
          }
        }

        if (config.format_validation && config.format_type == 'date' && raw_value) {
          const date_regex = /^\d{2}-\d{2}-\d{4}$/;
          if (!date_regex.test(raw_value)) {
            row_errors.push(`Invalid date format in "${config.column_name}". Expected format: DD-MM-YYYY`);
            row_isValid = false;
            continue;
          }
          const [d, m, y] = raw_value.split('-').map(Number);
          const date = new Date(y, m - 1, d);
          if (date.getFullYear() !== y || date.getMonth() + 1 !== m || date.getDate() !== d) {
            row_errors.push(`Invalid date value "${raw_value}" in "${config.column_name}"`);
            row_isValid = false;
            continue;
          }
        }

        if (config.data_type && raw_value) {
          let data_type_error = '';
          switch (config.data_type) {
            case 'number':
              if (!/^-?\d+$/.test(raw_value)) data_type_error = `Invalid number "${raw_value}" in "${config.column_name}"`;
              break;
            case 'float':
              if (!/^-?\d+(\.\d+)?$/.test(raw_value)) data_type_error = `Invalid float "${raw_value}" in "${config.column_name}"`;
              break;
            case 'boolean':
              if (!/^(true|false|1|0|yes|no)$/i.test(raw_value)) data_type_error = `Invalid True/False "${raw_value}" in "${config.column_name}"`;
              break;
            case 'object':
              try { JSON.parse(raw_value); } catch { data_type_error = `Invalid JSON in "${config.column_name}"`; }
              break;
            case 'string':
            default:
              break;
          }
          if (data_type_error) {
            row_errors.push(data_type_error);
            row_isValid = false;
            continue;
          }
        }

        // Uniqueness Check
        if (config.is_unique && raw_value) {
          const duplicateCount = data.filter((d: any) => (d[config.column_name]?.trim() ?? '') === raw_value).length;
          if (duplicateCount > 1) {
            row_errors.push(config.unique_error || `Duplicate value "${raw_value}" in column "${config.column_name}"`);
            row_isValid = false;
            continue;
          }
        }

        display_row[config.column_name] = raw_value;

        if (config.type === 'text') {
          if (config.regex_validation === true) {
            const value = String(raw_value ?? '').trim();
            const alpha_regex = /^[A-Za-z\s]+$/;

            if (value && !alpha_regex.test(value)) {
              row_errors.push(`Only alphabets are allowed in "${config.column_name}"`);
              row_isValid = false;
              continue;
            }
          }

          if (config.allowed_only && Array.isArray(config.allowed_only)) {
            const allowed = config.allowed_only.map((item: any) => item.toLowerCase().trim());
            const value = raw_value.toLowerCase().trim();

            if (!allowed.includes(value)) {
              row_errors.push(`Allowed values for "${config.column_name}": ${config.allowed_only.join(', ')}`);
              row_isValid = false;
              continue;
            }
          }
          parsed_row[config.return_as] = String(raw_value ?? '').trim();
        } else if (config.type === 'link' && config.linked_list && config.returning_key) {
          if (config.allowed_only && Array.isArray(config.allowed_only)) {
            const allowed = config.allowed_only.map((item: any) => item.toLowerCase().trim());
            const value = raw_value.toLowerCase().trim();

            if (!allowed.includes(value)) {
              row_errors.push(`Allowed values for "${config.column_name}": ${config.allowed_only.join(', ')}`);
              row_isValid = false;
              continue;
            }
          }

          const list = this.dropdown_data[config.linked_list] || [];
          const normalize = (val: any) => String(val ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
          const match = list.find((item: any) =>
            normalize(item?.title) == raw_value.toLowerCase() ||
            normalize(item?.name) == raw_value.toLowerCase()
          );

          if (!match) {
            row_errors.push(`No match found for "${raw_value}" in column "${config.column_name}"`);
            row_isValid = false;
            continue;
          }

          if (config.parent_column && config.parent_key) {
            const parentId = parsed_row[config.parent_column];
            if (parentId) {
              const itemParentId = config.parent_key.split('.').reduce((o: any, i: any) => o?.[i], match);
              if (String(itemParentId) !== String(parentId)) {
                row_errors.push(`Invalid relationship for "${raw_value}" in column "${config.column_name}"`);
                row_isValid = false;
                continue;
              }
            }
          }

          parsed_row[config.return_as] = match[config.returning_key];
        } else if (config.type === 'boolean_map') {
          const mappings = config.map_keys || [];
          const normalized_raw = raw_value.toLowerCase().trim();
          const match = mappings.find((m: any) => m.key.toLowerCase().trim() === normalized_raw);

          if (!match) {
            row_errors.push(`Allowed values for "${config.column_name}": ${mappings.map((m: any) => `"${m.key}"`).join(', ')}`);
            row_isValid = false;
            continue;
          }

          parsed_row[config.return_as] = match.value;
        } else if (config.type === 'array_link' && config.linked_list && config.returning_key) {
          const list = this.dropdown_data[config.linked_list] || [];
          const normalize = (val: any) => String(val ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
          const raw_values = String(raw_value).split(',').map(v => v.trim()).filter(v => v !== '' && v.toLowerCase() !== 'null' && v.toLowerCase() !== 'undefined');

          if (raw_values.length === 0) {
            row_errors.push(`"${config.column_name}" is required`);
            row_isValid = false;
            continue;
          }

          const mapped_ids: any[] = [];
          let col_invalid = false;

          for (const single of raw_values) {
            const normalized_single = normalize(single);

            if (config.allowed_only && Array.isArray(config.allowed_only)) {
              const allowed = config.allowed_only.map((item: any) => String(item).toLowerCase().trim());
              if (!allowed.includes(normalized_single)) {
                row_errors.push(`Allowed values for "${config.column_name}": ${config.allowed_only.join(', ')}`);
                row_isValid = false;
                col_invalid = true;
                break;
              }
            }

            const match = list.find((item: any) =>
              normalize(item?.title) === normalized_single ||
              normalize(item?.name) === normalized_single
            );

            if (!match) {
              row_errors.push(`No match found for "${single}" in column "${config.column_name}"`);
              row_isValid = false;
              col_invalid = true;
              break;
            }
            mapped_ids.push(match[config.returning_key]);
          }
          if (col_invalid) continue;

          parsed_row[config.return_as] = mapped_ids;
        }
      }

      // if (isValid) {
      //   parsed.push(parsed_row);
      //   display.push(display_row);
      // } else return;
      // Always add row to display so error report is visible even when all rows fail
      display.push({ ...row });

      if (row_isValid) {
        Object.assign(parsed_row, this.extra_data);
        parsed.push(parsed_row);
      } else {
        has_any_error = true;
      }
      this.error_report.push(row_errors.length > 0 ? row_errors : null);
    }

    if (has_any_error) {
      this.display_data = display;
      this.preview_data = parsed;
      this.file_upload_mode = false;
      this.is_uploading = false;
    } else {
      this.preview_data = parsed;
      this.display_data = display;
      this.file_upload_mode = false;
      this.error_message = '';
    }
  }

  emit_error(msg: string) {
    this.error_message = msg;
    this.error.emit(msg);
    this.file_upload_mode = true;
    this.is_uploading = false;
  }

  // on_submit() {
  //   try {
  //     if (this.preview_data.length > 0) {
  //     this.submit.emit({ data: this.preview_data});
  //     } else {
  //       throw new Error('No data to submit');
  //     }
  //   } catch (error: any) {
  //     this.emit_error(error.message || 'Something went wrong while submitting');
  //   } finally {
  //   }
  // }

  on_submit() {
    try {
      if (this.preview_data.length > 0) {

        const finalData = this.preview_data.map(row => ({ ...row, ...this.extra_data }));

        this.submit.emit({ data: finalData });

      } else {
        throw new Error('No data to submit');
      }
    } catch (error: any) {
      this.emit_error(error.message || 'Something went wrong while submitting');
    }
  }


  reupload() {
    this.preview_data = [];
    this.display_data = [];
    this.error_message = '';
    this.file_upload_mode = true;
    this.is_uploading = false;
  }

  validate_strict_field(config: any, raw_value: string, rowIndex: number): string | null {
    const type = config.strict_validation;
    if (!type) return null;

    const validator = this.STRICT_VALIDATORS[type];
    if (!validator) return null;

    const col = config.column_name;
    const val = raw_value.trim();
    const row = rowIndex + 1;

    if (type === 'gst') {
      if (!validator.regex_basic.test(val) || !validator.regex_format.test(val)) {
        return validator.message(val, row, col);
      }
      return null;
    }

    if (!validator.regex.test(val)) {
      return validator.message(val, row, col);
    }

    return null;
  }

  reset_bulk_state() {
    this.preview_data = [];
    this.display_data = [];
    this.error_message = '';
    this.file_upload_mode = true;
    this.is_uploading = false;

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  on_drag_over(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.is_dragging = true;
  }

  on_drag_leave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.is_dragging = false;
  }

  on_drop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.is_dragging = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const fakeEvent = { target: { files } };
    this.on_file_change(fakeEvent);
  }

  private validate_column_names(data: any[]): boolean {
    if (!data || data.length === 0) return false;

    const normalize = (val: string) => val.toLowerCase().trim();
    const uploaded_headers = Object.keys(data[0] || {}).map(normalize);

    let expected_headers_config = this.bulk_columns;
    if (this.excluded_columns.length > 0) {
      expected_headers_config = this.bulk_columns.filter(col => !this.excluded_columns.includes(col.column_name));
    }

    const expected_headers = expected_headers_config.map(c => normalize(c.column_name));
    const missing_columns = expected_headers.filter(col => !uploaded_headers.includes(col));
    const extra_columns = uploaded_headers.filter(col => !expected_headers.includes(col));

    if (missing_columns.length || extra_columns.length) {
      let error_msg = 'File format mismatch, Please upload the file using the original template\n\n';

      if (missing_columns.length) { error_msg += `Missing column(s): ${missing_columns.join(', ')}\n`; }
      // if (extra_columns.length) { error_msg += `Changed or extra column(s): ${extra_columns.join(', ')}\n`; }

      this.emit_error(error_msg);
      return false;
    }
    return true;
  }

  getValueByPath(obj: any, path: string): any {
    try {
      const keys = path.split('.');
      let result = obj;
      for (let k of keys) {
        if (result && typeof result === 'object' && k in result) {
          result = result[k];
        } else {
          return undefined;
        }
      }
      return result;
    } catch {
      return undefined;
    }
  }
}
